import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["searchSlot", "navWrapper", "header", "mainContent"];

  connect() {
    // Listen for Turbo frame renders
    document.addEventListener(
      "turbo:frame-render",
      this.handleFrameRender.bind(this),
    );

    // Also handle initial page load
    document.addEventListener("turbo:load", this.handlePageLoad.bind(this));

    // Set initial state
    this.updateFromCurrentPage();
  }

  disconnect() {
    document.removeEventListener(
      "turbo:frame-render",
      this.handleFrameRender.bind(this),
    );
    document.removeEventListener("turbo:load", this.handlePageLoad.bind(this));
  }

  handleFrameRender(event) {
    const frame = event.target;
    this.updateFromFrame(frame);
  }

  handlePageLoad() {
    this.updateFromCurrentPage();
  }

  updateFromCurrentPage() {
    // Look for meta elements in the current page
    const metaElement = document.querySelector("[data-page-meta]");
    if (!metaElement) return;

    this.applyPageMeta(metaElement.dataset);
  }

  updateFromFrame(frame) {
    // Look for meta element in the frame content
    const metaElement = frame.querySelector("[data-page-meta]");
    if (!metaElement) return;

    this.applyPageMeta(metaElement.dataset);
  }

  applyPageMeta(dataset) {
    // Update search visibility
    const showSearch = dataset.showSearch === "true";
    this.updateSearchVisibility(showSearch, dataset.searchPlaceholder);

    // Update active navigation
    if (dataset.activeNav) {
      this.updateActiveNav(dataset.activeNav);
    }

    // Update theme - default to "light" if not specified
    const theme = dataset.theme || "light";
    

    // Update header and nav visibility
    const hideHeader = dataset.hideHeader === "true";
    const hideBottomNav = dataset.hideBottomNav === "true";

    this.updateTheme(theme, hideBottomNav);
    this.updateVisibility(hideHeader, hideBottomNav);

    // Update container padding
    const removeContainerPadding = dataset.removeContainerPadding === "true";
    this.updateContainerPadding(removeContainerPadding);

    // Update the logo link to go to /about-us instead of / when on the home page
    this.updateLogoLink(dataset.logoLink);
  }

  updateSearchVisibility(show, placeholder = "Search") {
    if (!this.hasSearchSlotTarget) return;

    if (show) {
      this.searchSlotTarget.innerHTML = `
        <form action="/resources/search" method="get" class="pwa-search-form w-full">
          <div class="flex gap-1.5 group">
            <div class="relative flex-1">
              <input
                type="search"
                name="search_term"
                placeholder="${placeholder}"
                class="pwa-search-input w-full px-3 py-1.5 text-sm bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-oakwyn-gold"
                autocomplete="off">
            </div>
            <button type="submit" class="h-[34px] px-3 bg-oakwyn-black text-white rounded-[10px] hidden group-focus-within:flex items-center justify-center hover:bg-gray-800 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </form>
      `;
    } else {
      this.searchSlotTarget.innerHTML = "";
    }
  }

  updateActiveNav(activeKey) {
    if (!this.hasNavWrapperTarget) return;

    // Update the data attribute that the bottom nav controller watches
    this.navWrapperTarget.dataset.activeNav = activeKey;

    // Trigger a custom event that the nav controller can listen to
    this.navWrapperTarget.dispatchEvent(
      new CustomEvent("nav:updated", {
        detail: { activeNav: activeKey },
      }),
    );
  }

  updateTheme(theme, hideBottomNav) {
    const isDark = theme === "dark";

    // Dispatch theme change event for other controllers to listen to
    document.dispatchEvent(
      new CustomEvent("theme:changed", {
        detail: { theme, isDark },
      }),
    );

    // Handle direct DOM updates for theme
    this.updateHeaderTheme(isDark);
    this.updateMainContentTheme(isDark, hideBottomNav);
  }

  updateHeaderTheme(isDark) {
    // Update header background
    if (this.hasHeaderTarget) {
      const [removeClass, addClass] = isDark
        ? ["bg-white", "bg-black"]
        : ["bg-black", "bg-white"];
      this.headerTarget.classList.remove(removeClass);
      this.headerTarget.classList.add(addClass);
    }

    // Update logo circle background
    // const headerLogo = document.querySelector(".pwa-header-logo");
    // if (!headerLogo) return;

    // const classes = ["bg-white", "rounded-full", "p-[7px]"];

    // if (isDark) {
    //   headerLogo.classList.add(...classes);
    // } else {
    //   headerLogo.classList.remove(...classes);
    // }
  }

  updateLogoLink(link) {
    const headerLogo = document.querySelector(".pwa-header-logo");
    if (!headerLogo) return;
    headerLogo.href = link;
  }

  updateMainContentTheme(isDark, hideBottomNav) {
    // Update main content background
    if (this.hasMainContentTarget) {
      const [removeClass, addClass] = isDark
        ? ["bg-white", "bg-black"]
        : ["bg-black", "bg-white"];
      this.mainContentTarget.classList.remove(removeClass);
      this.mainContentTarget.classList.add(addClass);
    }

    // Also update the body background for consistency
    const body = document.body;

    if (hideBottomNav) {
      if (isDark) {
        body.classList.remove("bg-white");
        body.classList.add("bg-black");
      } else {
        body.classList.remove("bg-black");
        body.classList.add("bg-white");
      }
    } else {
      if (isDark) {
        body.classList.remove("bg-black");
        body.classList.add("bg-white");
      } else {
        body.classList.remove("bg-white");
        body.classList.add("bg-black");
      }
    }

    // console.log("*** hideBottomNav  ", hideBottomNav);
    // console.log("*** isDark", isDark);
    // console.log("*** body.classList", body.classList);

    // Also update the pwa body background for consistency
    const pwaContainer = document.getElementById("pwa-container");
    if (!pwaContainer) return;
    if (isDark) {
      pwaContainer.classList.remove("bg-white");
      pwaContainer.classList.add("bg-black");
    } else {
      pwaContainer.classList.remove("bg-black");
      pwaContainer.classList.add("bg-white");
    }

    
  }

  updateVisibility(hideHeader, hideBottomNav) {
    // Update header visibility
    const header = document.getElementById("app-header");
    if (header) {
      header.style.display = hideHeader ? "none" : "";
    }

    // Update bottom nav visibility
    const bottomNav = document.getElementById("bottom-nav-wrapper");
    if (bottomNav) {
      bottomNav.style.display = hideBottomNav ? "none" : "";
    }

    if (!this.hasMainContentTarget) {
      return;
    }

    // Update main content padding when bottom nav is hidden
    if (hideBottomNav) {
      this.mainContentTarget.classList.remove("pb-[88px]");
    } else {
      this.mainContentTarget.classList.add("pb-[88px]");
    }
  }

  updateContainerPadding(remove) {
    const container = document.getElementById("pwa-app");
    if (!container) return;

    const paddingClasses = [
      "px-8",
      "sm:px-12",
      "md:px-14",
      "lg:px-16",
      "pt-8",
      "sm:pt-11",
    ];

    if (remove) {
      // Remove all padding classes
      container.classList.remove(...paddingClasses);
    } else {
      // Add padding classes back
      container.classList.add(...paddingClasses);
    }
  }
}
